import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {
  useCaseDetail,
  useDocumentTitle,
  usePaymentsByTask,
  usePricing,
  useQuotesByTask,
  useTaskDetail,
  useCanAsMember,
} from '@/hooks';
import { extractList } from '@/lib/apiList';
import {
  cancelTask,
  downloadPayinReceipt,
  errorMessage,
  sendDiligencia,
  updateTask,
} from '@/lib/recepthor-api';
import { useAuth } from '@/app/AuthContext.jsx';
import { qk } from '@/lib/queryKeys';
import { useConfirm, useToast } from '@/ui';
import { diligenciaDisplay, formatChileanDateFull, titleCase } from '@/lib';
import { CheckoutModal } from '@/shared';

import {
  DiligenciaDetalle,
  DiligenciaDetalleCargando,
} from './DiligenciaDetalleVista';
import NuevaDiligenciaModal from './NuevaDiligenciaModal';
import SolicitudCotizacionesResumen from './SolicitudCotizacionesResumen';
import { formatDeadlineDate as formatDeadline } from '@/lib/dateUtils';
import { actuationLabel } from '@/lib/taskActuation.js';
import { offerBreakdownLines } from '@/lib/priceBreakdown.js';
import './DiligenciaDetalleVista.css';

// VerDiligencia — the by-id detail PAGE (data + assemble). It hydrates the task
// (getTask + getCase + quotes + payments), derives `estado` from the
// diligenciaDisplay source of truth, maps the real quotes/movements onto
// DiligenciaDetalle's props, and wires the actions: pay → CheckoutModal,
// editar/reenviar → the NuevaDiligenciaModal, enviar → POST /tasks/:id/send.
// Creation of a NEW diligencia lives in the list/causa modal, not here.

// The four quote states, and the only place the web spells them. Each one names
// what its actor did: the system invites, the receptor answers. Whether an offer
// was the one chosen is NOT among them — the firm chooses by paying, so that
// reading comes from the payment, below.
const QUOTE_VIEW = {
  INVITE_PENDING: { tone: 'mute', label: 'Por enviar' },
  INVITED: { tone: 'info', label: 'Enviada' },
  OFFERED: { tone: 'live', label: 'Oferta recibida' },
  DECLINED: { tone: 'err', label: 'Rechazada' },
};
// Actionable first, then invited, pending delivery, declined. Stable within a
// group.
const QUOTE_SORT = {
  OFFERED: 0,
  INVITED: 1,
  INVITE_PENDING: 2,
  DECLINED: 3,
};
const quoteRank = (s) => QUOTE_SORT[s] ?? 9;
const CLOSED_QUOTE = ['DECLINED'];

// diligenciaDisplay.label → the estado key the vista switches on.
const ESTADO_BY_LABEL = {
  Borrador: 'borrador',
  Cotizando: 'cotizando',
  'Listo para pagar': 'listo',
  'Procesando pago': 'procesando',
  'Reintentar pago': 'reintentar',
  'En curso': 'encurso',
  Atrasada: 'encurso',
  Finalizada: 'finalizada',
  Expirada: 'expirada',
  Cancelada: 'cancelada',
};
// Pre-payment estados a firm can cancel. Excludes en-curso (paid) and the
// terminal states. An expired pre-payment diligencia (estado 'expirada') IS
// cancellable — that's how the causa gets freed. The backend is the final
// authority (409 if a payin is in flight); the UI just offers the action.
const CANCELABLE_ESTADOS = new Set([
  'borrador',
  'cotizando',
  'sinofertas',
  'listo',
  'procesando',
  'reintentar',
  'expirada',
]);
const STEP_BY_ESTADO = {
  borrador: 0,
  cotizando: 1,
  sinofertas: 1,
  listo: 2,
  procesando: 2,
  reintentar: 2,
  encurso: 3,
  finalizada: 4,
  expirada: 4,
  cancelada: 4,
};

export default function VerDiligencia() {
  useDocumentTitle('Diligencia');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { setBreadcrumb, setPills } = useOutletContext();
  const { organizationId } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  // /diligencias/nueva has its own static route (the create modal host), so
  // :id here is always a real task id.
  const taskId = params.id ?? null;

  // GET /tasks/:id is org-scoped: a foreign / missing task 404s → clean
  // "no encontrada" instead of a half-empty page.
  const {
    data: taskData,
    error: taskFetchError,
    isError: taskIsError,
    isLoading: taskLoading,
  } = useTaskDetail(taskId);
  const taskNotFound =
    Boolean(taskId) &&
    taskIsError &&
    [403, 404].includes(taskFetchError?.status);

  const caseId = taskData?.caseId ?? null;
  const { data: caseInfo, isLoading: caseLoading } = useCaseDetail(caseId, {
    enabled: Boolean(caseId),
  });
  const { data: quotesData, isError: offersFailed } = useQuotesByTask(taskId);
  const quotes = Array.isArray(quotesData?.results) ? quotesData.results : [];

  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [payQuoteId, setPayQuoteId] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  // Unified model: hydrated task + case.
  const d = useMemo(() => {
    const t = taskData || {};
    const c = caseInfo || {};
    return {
      caseId: t.caseId ?? null,
      // Encargado inherited from the causa (derived onto the task read).
      assigneeName: t.assigneeName ?? null,
      rol: c.rol ?? null,
      caratula: c.title ?? null,
      courtName: c.courtName ?? null,
      tribunalName: t.tribunalName ?? c.tribunalName ?? null,
      deadline: t.deadline ?? '',
      deadlineExpiresAt: t.deadlineExpiresAt,
      comuna: t.commune ?? null,
      region: t.region ?? null,
      address: t.address ?? '',
      detalle: t.addressDetails ?? '',
      notes: t.notes ?? '',
      actuacion: actuationLabel(t) ?? t.actuacion ?? 'Notificación',
      // The served phase, and the only thing the readings and the action gates
      // below run on. Absent (the task read still in flight) it stays null,
      // which reads as the neutral fallback instead of claiming a borrador.
      stage: t.stage ?? null,
      // Checkout link the live payin froze, served by the task read. It is what
      // lets a payer who closed the Pasarela reach the charge again instead of
      // waiting on a webhook that needs their payment first. Null when no
      // attempt is open, which is every stage but PAYMENT_PROCESSING and
      // PAYMENT_REJECTED.
      paymentLink: t.paymentLink ?? null,
    };
  }, [taskData, caseInfo]);

  const stage = d.stage;

  // Paid phase: the charge committed, so the adjudicated quote is read off the
  // PAYIN movement (GET /payments?taskId) plus the task's own paid fields. A
  // rejected payment belongs here too — the diligencia stays committed to its
  // chosen offer while the same charge is retried.
  const isPaidPhase = [
    'PAYMENT_PROCESSING',
    'PAYMENT_REJECTED',
    'IN_PROGRESS',
    'CLOSED',
  ].includes(stage);
  // GET /payments needs payment:read (an owner always has it). Without it the
  // page does not ask: the foot shows the phase with no amounts and no
  // comprobante, and no read error.
  const canReadPayments = useCanAsMember('payment:read');
  const {
    data: paymentsData,
    isError: paymentsFailed,
    isFetching: paymentsFetching,
    refetch: refetchPayments,
    receiptPolling,
  } = usePaymentsByTask(taskId, {
    enabled: Boolean(taskId) && isPaidPhase && canReadPayments,
    // Only once the charge settled: PAYMENT_PROCESSING and PAYMENT_REJECTED
    // have no comprobante yet, and nothing to wait for.
    awaitReceipt: stage === 'IN_PROGRESS' || stage === 'CLOSED',
  });
  // A failed read is not "this diligencia has no payment". Both would leave the
  // paid phase with no receptor, no total and no comprobante, and the payer
  // cannot tell them apart from the rendered amounts alone, which show a dash
  // either way. So the panel says which one it is, and it says it where the
  // amounts would be: a toast leaves and the dashes stay behind it. Only with
  // no data: a failed background re-read keeps the amounts it already has.
  const payError = useMemo(
    () =>
      paymentsFailed && !paymentsData
        ? { busy: paymentsFetching, onRetry: () => refetchPayments() }
        : null,
    [paymentsFailed, paymentsData, paymentsFetching, refetchPayments],
  );
  const adjudicada = useMemo(() => {
    // Newest-first list; a FAILED attempt can precede the live one, so the
    // non-FAILED PAYIN leads (the DB allows at most one live attempt).
    const movements = extractList(paymentsData);
    const payin =
      movements.find((p) => p.type === 'PAYIN' && p.status !== 'FAILED') ??
      movements.find((p) => p.type === 'PAYIN') ??
      null;
    if (!payin) return null;
    const t = taskData || {};
    const wq = quotes.find((q) => q.id === payin.quoteId);
    const fechaIso = wq?.dateOffered ?? t.dateOffered ?? null;
    return {
      paymentId: payin.paymentId ?? null,
      quoteId: payin.quoteId,
      name: wq?.receptor?.name || t.payeeName || 'Receptor adjudicado',
      price: Number(wq?.price) || Number(t.paidAmount) || 0,
      // The charged total: honorarios + fee (task paidTotal, frozen at pay
      // time) with the movement's plannedAmount as the fallback.
      total: Number(t.paidTotal) || Number(payin.plannedAmount) || 0,
      fecha: fechaIso ? formatChileanDateFull(fechaIso) : null,
      dateOfferedIso: fechaIso,
      // Own comprobante archived in S3 (the movement's voucher) — gates the
      // descarga; false while archiving is in flight.
      hasReceipt: payin.hasReceipt === true,
    };
  }, [paymentsData, quotes, taskData]);

  // Without payment:read the payments are not read, so there is no adjudicated
  // row; the task read still says when the offer was made, which keeps the
  // committed date and the late notice. With access nothing changes.
  const offeredDateIso = canReadPayments
    ? (adjudicada?.dateOfferedIso ?? null)
    : (adjudicada?.dateOfferedIso ?? taskData?.dateOffered ?? null);

  const display = useMemo(
    () =>
      diligenciaDisplay(d.stage, d.deadline, Date.now(), {
        deadlineExpiresAt: d.deadlineExpiresAt,
        dateOffered: offeredDateIso,
      }),
    [d.stage, d.deadline, d.deadlineExpiresAt, offeredDateIso],
  );
  // A DRAFT past its plazo derives "Expirada" (SOT rule 3) — right for labels
  // and lanes, but this page IS the borrador flow: it was never sent, so the
  // offers-flavored expirada UI would be false. Keep estado 'borrador' (info +
  // edición, stepper at paso 1) and let the ESTADO card show the draftExpired
  // variant ("Expiró sin enviar" + editar/reenviar).
  const isDraft = stage === 'DRAFT';
  const estado = isDraft
    ? 'borrador'
    : ESTADO_BY_LABEL[display.label] || 'cotizando';
  const draftExpired = isDraft && display.expired;
  // expirada/cancelada land the stepper on "Cerrada" (STEP_BY_ESTADO) without
  // ever passing through Por pagar / En curso — the Stepper must not paint
  // those as done.
  const neverPaid = estado === 'expirada' || estado === 'cancelada';
  // Only "Atrasada" (dateOffered passed, plazo vigente) overlays as late; a
  // deadline-passed IN_PROGRESS reads Finalizada (cierre natural), not late.
  const late = display.label === 'Atrasada';
  const currentStep = STEP_BY_ESTADO[estado] ?? 1;
  // Payable while no charge is in flight: an offer can be accepted (QUOTING)
  // or already chosen and awaiting payment (PAYMENT_DUE).
  const canPay = ['QUOTING', 'PAYMENT_DUE'].includes(stage) && !display.expired;

  // Cheapest offer received → "Recomendada" (autopay "más barata" heuristic).
  const recommendedId = useMemo(() => {
    const offered = quotes.filter(
      (q) => String(q.status || '').toUpperCase() === 'OFFERED',
    );
    if (!offered.length) return null;
    return offered.reduce((a, b) =>
      (Number(a.price) || Infinity) <= (Number(b.price) || Infinity) ? a : b,
    ).id;
  }, [quotes]);

  // Default the picker selection to the recommended (or first payable) offer.
  useEffect(() => {
    if (estado === 'listo' && !selectedOfferId) {
      setSelectedOfferId(recommendedId);
    }
  }, [estado, recommendedId, selectedOfferId]);

  const offers = useMemo(() => {
    const rows = [...quotes].sort(
      (a, b) =>
        quoteRank(String(a.status || '').toUpperCase()) -
        quoteRank(String(b.status || '').toUpperCase()),
    );
    return rows.map((q) => {
      const st = String(q.status || '').toUpperCase();
      const view = QUOTE_VIEW[st] || { tone: 'info', label: st || '—' };
      const isChosen = adjudicada && q.id === adjudicada.quoteId;
      const closedNoPay = ['expirada', 'cancelada'].includes(estado);
      return {
        id: q.id,
        name: q.receptor?.name || 'Receptor de la corte',
        sub: [
          q.receptor?.primaryEmail,
          q.dateOffered
            ? `ofrece ${formatChileanDateFull(q.dateOffered)}`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        tone: view.tone,
        label: view.label,
        price: Number(q.price) || 0,
        // How the receptor priced it per recipient (REC-1983), if it did.
        breakdown: offerBreakdownLines(
          q.priceBreakdown,
          q.recipients ?? taskData?.recipients,
        ),
        recommended: estado === 'listo' && q.id === recommendedId,
        chosen: Boolean(isChosen),
        chosenTag: isChosen ? (closedNoPay ? 'Sin pagar' : 'Adjudicada') : null,
        chosenTone: closedNoPay ? 'err' : 'ok',
        closed: CLOSED_QUOTE.includes(st),
        payable: canPay && st === 'OFFERED',
      };
    });
  }, [quotes, adjudicada, estado, recommendedId, canPay, taskData]);

  // Pay breakdown for the phases that still CHARGE — same source as
  // CheckoutModal (GET /pricing/calculate), so the panel numbers match the
  // checkout exactly. Paid phases never read the live tariff: they render the
  // persisted record below.
  const showPricing = ['listo', 'reintentar', 'procesando'].includes(estado);
  const { data: pricingData } = usePricing({
    enabled: Boolean(taskId) && showPricing,
  });
  const calc = pricingData?.data ?? pricingData ?? null;

  // The offer the pay panel describes: the picker selection (listo) or the
  // adjudicada (paid phases).
  const selectedQuote = quotes.find((q) => q.id === selectedOfferId) || null;
  // Derived from the query, not copied when Pagar opens: after a price change
  // the modal has to show the refetched price.
  const payQuote = useMemo(() => {
    const q = quotes.find((x) => x.id === payQuoteId);
    if (!q) return null;
    return {
      id: q.id,
      name: q.receptor?.name || 'Receptor',
      price: Number(q.price) || 0,
      expectedPrice: q.price,
      fecha: q.dateOffered ? formatChileanDateFull(q.dateOffered) : null,
    };
  }, [quotes, payQuoteId]);
  const panelOffer =
    estado === 'listo'
      ? selectedQuote
      : quotes.find((q) => q.id === adjudicada?.quoteId) || null;
  const chosen = panelOffer
    ? {
        name: panelOffer.receptor?.name || adjudicada?.name || 'Receptor',
        price: Number(panelOffer.price) || 0,
      }
    : adjudicada
      ? { name: adjudicada.name, price: adjudicada.price }
      : null;

  const pay = useMemo(() => {
    // Paid phases render the persisted record, frozen at pay time: a tariff
    // change after payment must not rewrite the breakdown the payer saw. The
    // frozen serviceFee carries the IVA included with no net/IVA split, so the
    // foot shows it as one component (pay.serviceFee) instead of fee + IVA.
    if (isPaidPhase) {
      const frozen = taskData?.paidBreakdown ?? null;
      const honorarios = frozen?.price ?? adjudicada?.price ?? null;
      const total = adjudicada?.total ?? null;
      const paidTotal = Number(taskData?.paidTotal);
      const remainder =
        honorarios > 0 && paidTotal > 0 ? paidTotal - honorarios : null;
      return {
        honorarios,
        fee: null,
        iva: null,
        serviceFee: frozen?.serviceFee ?? null,
        legacyFee: !frozen && remainder > 0 ? remainder : null,
        hideBreakdown: !frozen && !(remainder > 0),
        total,
      };
    }
    const honorarios = chosen?.price ?? null;
    const feeNet = calc?.price?.netAmount ?? null;
    const feeIva = calc?.price?.ivaAmount ?? null;
    const feeTotal = calc?.price?.totalAmount ?? null;
    const total =
      honorarios != null && feeTotal != null ? honorarios + feeTotal : null;
    return { honorarios, fee: feeNet, iva: feeIva, serviceFee: null, total };
  }, [chosen, calc, isPaidPhase, adjudicada, taskData]);

  const offeredDateText = canReadPayments
    ? adjudicada?.fecha || null
    : offeredDateIso
      ? formatChileanDateFull(offeredDateIso)
      : null;

  useEffect(() => {
    setBreadcrumb([
      { label: 'Diligencias', onClick: () => navigate('/diligencias') },
      d.rol || 'Diligencia',
    ]);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills, navigate, d.rol]);

  // ── actions ──
  async function runSend() {
    setBusy(true);
    const toastId = toast({
      kind: 'loading',
      message: 'Solicitando cotizaciones…',
      dismissLabel: 'en curso',
    });
    try {
      await sendDiligencia(taskId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.taskDetail(taskId) }),
        queryClient.invalidateQueries({ queryKey: qk.quotes.byTask(taskId) }),
      ]);
      toast.update(toastId, {
        kind: 'success',
        message: 'Cotizaciones solicitadas.',
        duration: 4000,
      });
    } catch (err) {
      console.error('sendDiligencia failed', err);
      toast.update(toastId, {
        kind: 'err',
        message: errorMessage(
          err,
          'No se pudieron solicitar las cotizaciones.',
        ),
        duration: 0,
        actionLabel: 'Reintentar',
        action: runSend,
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleEnviar() {
    if (!taskId || busy) return;
    // La actuación es obligatoria para pedir cotizaciones (REC-1966). Un
    // borrador de antes no la tiene: se completa en el formulario.
    if (!taskData?.actuationType || !taskData?.recipients?.length) {
      toast({
        kind: 'err',
        message:
          'Antes de solicitar cotizaciones, indica la actuación y los destinatarios.',
        actionLabel: 'Editar',
        action: () => setModalOpen(true),
      });
      return;
    }
    const ok = await confirm({
      title: 'Solicitar cotizaciones',
      body: (
        <>
          <p className="confirm-lead">
            Se envía la solicitud a los receptores de la corte. Es irreversible:
            tras enviar, los datos de la diligencia quedan fijos.
          </p>
          <SolicitudCotizacionesResumen
            data={{
              ...d,
              rol: d.rol ?? taskData?.caseRol,
              caratula:
                d.caratula ??
                (taskData?.caseTitle
                  ? titleCase(taskData.caseTitle)
                  : undefined),
              courtName: d.courtName ?? taskData?.courtName,
              commune: d.comuna,
              addressDetails: d.detalle,
              actuationType: taskData?.actuationType,
              actuationTypeOther: taskData?.actuationTypeOther,
              conservador: taskData?.conservador,
              addressType: taskData?.addressType,
              recipients: taskData?.recipients,
            }}
          />
        </>
      ),
      confirmLabel: 'Solicitar cotizaciones',
      confirmVariant: 'seal',
    });
    if (ok) runSend();
  }

  async function handleEditSubmit(payload) {
    if (!taskId || busy) return;
    setBusy(true);
    const toastId = toast({
      kind: 'loading',
      message: 'Guardando cambios…',
      dismissLabel: 'en curso',
    });
    try {
      await updateTask(taskId, payload);
      await queryClient.invalidateQueries({ queryKey: qk.taskDetail(taskId) });
      setModalOpen(false);
      toast.update(toastId, {
        kind: 'success',
        // Saving revives an expired draft but sends nothing — point the user
        // at the send step so "Volver a cotizar" doesn't read as already sent.
        message: draftExpired
          ? 'Borrador actualizado. Solicita cotizaciones para enviarlo.'
          : 'Cambios guardados.',
        duration: 4000,
      });
    } catch (err) {
      console.error('updateTask failed', err);
      toast.update(toastId, {
        kind: 'err',
        message: errorMessage(err, 'No se pudieron guardar los cambios.'),
        duration: 0,
      });
    } finally {
      setBusy(false);
    }
  }

  function handlePay() {
    // Pay opens the checkout on the offer the picker selected. Retry opens it
    // on the one the failed charge was made against, which the movement names:
    // a diligencia can carry several offers, and taking the first one would
    // retry against a different receptor than the one the firm committed to.
    // With no committed quote — the movements read failed — nothing opens,
    // because charging the wrong offer is worse than not charging.
    const q =
      estado === 'listo'
        ? selectedQuote
        : quotes.find((x) => x.id === adjudicada?.quoteId);
    if (q) setPayQuoteId(q.id);
  }

  async function runCancel({ reason, loading, success, fail, to } = {}) {
    setBusy(true);
    const toastId = toast({
      kind: 'loading',
      message: loading ?? 'Cancelando diligencia…',
      dismissLabel: 'en curso',
    });
    try {
      await cancelTask(taskId, reason ? { reason } : undefined);
      // Cancelling frees the causa and drops the diligencia from the active set:
      // refresh everything, then land on `to` (the re-quote flow) or return to
      // the causa (now free) / the list.
      await queryClient.invalidateQueries();
      toast.update(toastId, {
        kind: 'success',
        message: success ?? 'Diligencia cancelada. La causa quedó libre.',
        duration: 4000,
      });
      if (to) navigate(to.pathname, { state: to.state });
      else navigate(d.caseId ? `/causas/${d.caseId}` : '/diligencias');
    } catch (err) {
      console.error('cancelTask failed', err);
      toast.update(toastId, {
        kind: 'err',
        message: errorMessage(
          err,
          fail ?? 'No se pudo cancelar la diligencia.',
        ),
        duration: 0,
      });
    } finally {
      setBusy(false);
    }
  }

  // Causa summary shared by the close-flavored confirms (cancel / archive /
  // volver a cotizar).
  const resumen = (
    <dl className="confirm-summary">
      <div className="confirm-summary__row">
        <dt>Causa</dt>
        <dd>{d.rol || '—'}</dd>
      </div>
      <div className="confirm-summary__row">
        <dt>Carátula</dt>
        <dd>{d.caratula || '—'}</dd>
      </div>
    </dl>
  );

  async function handleCancelar() {
    if (!taskId || busy) return;
    // Archivar: an expirada closes through the same cancel
    // transition, framed as archiving — quotes went out but nothing was
    // charged. The reason distinguishes it from a manual cancel in the audit
    // trail.
    if (estado === 'expirada') {
      const ok = await confirm({
        title: 'Archivar diligencia',
        body: (
          <>
            <p className="confirm-lead">
              Se archiva esta diligencia expirada y se cierran sus cotizaciones
              pendientes. La causa queda libre para volver a cotizar. No se
              cobró nada. Esta acción no se puede deshacer.
            </p>
            {resumen}
          </>
        ),
        confirmLabel: 'Archivar',
        confirmVariant: 'danger',
      });
      if (ok) {
        runCancel({
          reason: 'archivada: diligencia expirada',
          loading: 'Archivando diligencia…',
          success: 'Diligencia archivada. La causa quedó libre.',
          fail: 'No se pudo archivar la diligencia.',
        });
      }
      return;
    }
    // Archivar: an expired draft closes through the same cancel
    // transition, framed as archiving — nothing was sent or charged. The
    // reason distinguishes it from a manual cancel in the audit trail.
    if (draftExpired) {
      const ok = await confirm({
        title: 'Archivar diligencia',
        body: (
          <>
            <p className="confirm-lead">
              Se archiva este borrador expirado y la causa queda libre para
              volver a cotizar. No se envió nada a los receptores ni se cobró.
              Esta acción no se puede deshacer.
            </p>
            {resumen}
          </>
        ),
        confirmLabel: 'Archivar',
        confirmVariant: 'danger',
      });
      if (ok) {
        runCancel({
          reason: 'archivada: borrador expirado',
          loading: 'Archivando diligencia…',
          success: 'Diligencia archivada. La causa quedó libre.',
          fail: 'No se pudo archivar la diligencia.',
        });
      }
      return;
    }
    const ok = await confirm({
      title: 'Cancelar diligencia',
      body: (
        <>
          <p className="confirm-lead">
            Se cancela esta diligencia y se cierran las cotizaciones pendientes.
            La causa queda libre para volver a cotizar. Esta acción no se puede
            deshacer.
          </p>
          {resumen}
        </>
      ),
      confirmLabel: 'Cancelar diligencia',
      confirmVariant: 'danger',
    });
    if (ok) runCancel();
  }

  const canCancel = CANCELABLE_ESTADOS.has(estado);

  // Navigation payload for the prefilled create flow (same contract as the
  // board's goNueva). The case query can still be in flight — or
  // have failed, useCaseDetail degrades to null — when a CTA is clicked, so
  // fall back to the task's own case fields (guaranteed once the detail
  // renders) and the locked causa never opens half-empty.
  const nuevaStateFromTask = () => ({
    caseId: d.caseId,
    rol: d.rol ?? taskData?.caseRol,
    caratula:
      d.caratula ??
      (taskData?.caseTitle ? titleCase(taskData.caseTitle) : undefined),
    courtName: d.courtName ?? taskData?.courtName,
    fromTaskId: taskId,
    // Keeps this detail mounted under the create modal (VD-8).
    background: location,
  });

  // Volver a cotizar: a finalizada is terminal, so the causa is
  // already free — no transition needed; open the prefilled create flow.
  const handleVolverACotizar = () =>
    navigate('/diligencias/nueva', { state: nuevaStateFromTask() });

  // Volver a cotizar on an expirada: unlike a finalizada, the causa
  // is still occupied by the expired diligencia — close it first (the cancel
  // transition frees it; the reason keeps the audit trail distinguishable from
  // a manual cancel), then open the prefilled create flow. The backend 409s if
  // a payin is mid-flight; runCancel surfaces that as the error toast.
  async function handleVolverACotizarExpirada() {
    if (!taskId || busy) return;
    const ok = await confirm({
      title: 'Volver a cotizar',
      body: (
        <>
          <p className="confirm-lead">
            Se archiva esta diligencia expirada (la causa queda libre) y se abre
            una nueva con los mismos datos para solicitar cotizaciones otra vez.
            No se cobró nada.
          </p>
          {resumen}
        </>
      ),
      confirmLabel: 'Volver a cotizar',
      confirmVariant: 'seal',
    });
    if (ok) {
      runCancel({
        reason: 'volver a cotizar: diligencia expirada',
        loading: 'Archivando la diligencia expirada…',
        success: 'Diligencia archivada. La causa quedó libre.',
        fail: 'No se pudo archivar la diligencia expirada.',
        to: { pathname: '/diligencias/nueva', state: nuevaStateFromTask() },
      });
    }
  }

  if (taskNotFound) {
    return (
      <main className="page dd-page reveal">
        <div className="detail-notfound">
          <h1 className="page-title">Diligencia no encontrada</h1>
          <p className="detail-notfound__body">
            Esta diligencia no existe o no pertenece a la organización activa.
            Si cambiaste de organización, vuelve al listado para ver las
            diligencias que sí te corresponden.
          </p>
          <button
            type="button"
            className="t-btn t-btn--ghost"
            onClick={() => navigate('/diligencias')}
          >
            Volver a Diligencias
          </button>
        </div>
      </main>
    );
  }

  if (!taskId) return null;

  // The first read of the task, and of its causa once the task names it. The
  // view drew every field as "—" until both came back; a skeleton says it is
  // loading instead. Background re-reads keep the data they have.
  if (taskLoading || caseLoading) return <DiligenciaDetalleCargando />;

  return (
    <>
      <DiligenciaDetalle
        caratula={d.caratula}
        rol={d.rol}
        tribunalName={d.tribunalName}
        encargado={d.assigneeName}
        actuacion={d.actuacion}
        deadlineText={formatDeadline(d.deadline)}
        offeredDateText={offeredDateText}
        estado={estado}
        draftExpired={draftExpired}
        currentStep={currentStep}
        neverPaid={neverPaid}
        late={late}
        alertText={late ? display.alert : null}
        offers={offers}
        offersCount={offers.length}
        offersFailed={offersFailed}
        courtName={d.courtName}
        selectedOfferId={selectedOfferId}
        onSelectOffer={setSelectedOfferId}
        chosen={chosen}
        pay={pay}
        payError={payError}
        paymentsAvailable={canReadPayments}
        paymentLink={
          estado === 'procesando' || estado === 'reintentar'
            ? d.paymentLink
            : null
        }
        draft={{
          actuacion: d.actuacion,
          address: d.address,
          detalle: d.detalle,
          comuna: [d.comuna, d.region].filter(Boolean).join(' · '),
          deadlineText: formatDeadline(d.deadline),
          notas: d.notes,
        }}
        busy={busy}
        onPay={handlePay}
        onEnviar={handleEnviar}
        onRecotizar={() => setModalOpen(true)}
        onEditar={() => setModalOpen(true)}
        onVerCausa={
          d.caseId ? () => navigate(`/causas/${d.caseId}`) : undefined
        }
        onCancelar={canCancel ? handleCancelar : undefined}
        onNueva={
          !d.caseId
            ? undefined
            : estado === 'finalizada'
              ? handleVolverACotizar
              : estado === 'expirada'
                ? handleVolverACotizarExpirada
                : undefined
        }
        comprobanteGenerando={receiptPolling}
        onComprobante={
          // Handler presence = availability: the Vista renders the disabled
          // "Comprobante no disponible" state until the own comprobante is
          // archived (hasReceipt). Download is fetch + blob save against the
          // org-gated receipt endpoint, keyed by the movement's paymentId.
          // Pass organizationId explicitly (like Pagos / Facturación) so the
          // X-Organization-Id header never depends on the orgContext
          // singleton.
          adjudicada?.hasReceipt && adjudicada.paymentId
            ? () =>
                downloadPayinReceipt(adjudicada.paymentId, {
                  organizationId,
                  filename: d.rol ? `comprobante-${d.rol}.pdf` : undefined,
                }).catch((err) =>
                  toast({
                    kind: 'err',
                    message:
                      err?.status === 404
                        ? 'Comprobante no disponible.'
                        : errorMessage(
                            err,
                            'No se pudo descargar el comprobante.',
                          ),
                  }),
                )
            : undefined
        }
      />

      <NuevaDiligenciaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode="edit"
        lockedCase={{
          caseId: d.caseId,
          rol: d.rol,
          caratula: d.caratula,
          courtName: d.courtName,
          tribunalName: d.tribunalName,
        }}
        initial={{
          caseId: d.caseId,
          comuna: d.comuna,
          region: d.region,
          address: d.address,
          detalle: d.detalle,
          deadline: d.deadline,
          notes: d.notes,
          actuationType: taskData?.actuationType,
          actuationTypeOther: taskData?.actuationTypeOther,
          conservador: taskData?.conservador,
          addressType: taskData?.addressType,
          recipients: taskData?.recipients,
        }}
        onSubmit={handleEditSubmit}
        isSubmitting={busy}
      />

      <CheckoutModal
        open={Boolean(payQuote)}
        onClose={() => setPayQuoteId(null)}
        code={d.rol || undefined}
        title={d.caratula || undefined}
        quote={payQuote}
        siblings={Math.max(
          0,
          quotes.filter(
            (q) => String(q.status || '').toUpperCase() === 'OFFERED',
          ).length - 1,
        )}
      />
    </>
  );
}
